import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import axios from "axios";
import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  FormGroup,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Autocomplete,
  Paper,
  Checkbox,
  TextareaAutosize,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import StyledTable from "./StyledTable";
import DrawerComponent from "./common/DrawerComponent";
import { UserContext } from "../UserContext";

// PDF Viewer component (unchanged)
const PdfViewer = ({ blobUrl, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [blobUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "70vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading PDF...
          </Typography>
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            Error loading PDF: {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.open(blobUrl, "_blank")}
          >
            Open in New Tab Instead
          </Button>
        </Box>
      ) : (
        <iframe
          src={`${blobUrl}#view=FitH`}
          width="100%"
          height="100%"
          frameBorder="0"
          title="PDF Preview"
          onError={() => setError("Failed to load the PDF")}
        />
      )}
    </Box>
  );
};

const formFields = [
  { id: "client_details", label: "Client Details", type: "text" },
  { id: "Kind_Attn", label: "Kind Attn", type: "text" },
  { id: "Client", label: "Client", type: "text" },
  { id: "Website", label: "Website", type: "text" },
  { id: "Project", label: "Project", type: "text" },
  { id: "Duration", label: "Duration", type: "text" },
  { id: "summary", label: "Summary", type: "text", multiline: true, rows: 4 },
  { id: "invoice_items", label: "Invoice Items", type: "text" },
];

const allPossibleRoles = [
  "Project Manager",
  "Full Stack Developer",
  "Frontend Developer",
  "Backend Developer",
  "Mobile App Developer",
  "UI/UX Designer",
  "QA/Test Engineer",
  "DevOps Engineer",
  "Business Analyst",
  "Product Owner",
  "Scrum Master",
  "Technical Lead",
  "Data Engineer",
  "Machine Learning Engineer",
  "Cloud Architect",
  "Security Engineer",
  "Database Administrator",
  "System Administrator",
  "Technical Writer",
  "Graphic Designer",
  "Blockchain Developer",
  "IoT Developer",
  "Game Developer",
  "Salesforce Developer",
  "SAP Consultant",
];

const platformOptions = ["Web Application", "Mobile Application"];

const days = [
  {
    value: "Days",
    label: "Days",
  },
  {
    value: "Week",
    label: "Week",
  },
  {
    value: "Month",
    label: "Month",
  },
  {
    value: "Year",
    label: "Year",
  },
];

const clauseOptions = [
  "Clauses",
  "Major Add-ons(Quoted Separately if required)",
];

const GenerateTCO = () => {
  const { role, user_id } = useContext(UserContext);
  const [userRoleId, setUserRoleId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [currentPdfBlob, setCurrentPdfBlob] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [sendMailModalOpen, setSendMailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    recipientEmail: "",
    ccEmail: "",
    emailContent: "",
  });

  // State for AMC Checkbox
  const [isAmcChecked, setIsAmcChecked] = useState(false);

  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  const [invoiceData, setInvoiceData] = useState({});

  // FIX: Validation Logic with safe defaults
  const isFormValid = useMemo(() => {
    // Destructure with default empty arrays to prevent crash on undefined
    const {
      scope_work = [],
      platform_delivery = [],

      technical_details = [],
      timeline = [],

      team_resource = [],
      amc = [],
      pricing = [],
      pricing_notes = [],
      payment_terms = [],
      terms_conditions = [],
      Submission,
      ...rest
    } = invoiceData;

    // 1. Check Basic Fields
    const basicFieldsValid = formFields.every((field) => {
      const val = rest[field.id];
      return val && String(val).trim() !== "";
    });

    // 2. Check Submission Date
    const submissionValid = !!Submission;

    // 3. Check Scope of Work
    const scopeValid =
      scope_work.length > 0 &&
      scope_work.every((item) => item.title && item.description);

    // 4. Platform & Delivery
    const platformValid =
      platform_delivery.length > 0 &&
      platform_delivery.every((item) => item.platform && item.description);

    // 6. Technical Details
    const techValid =
      technical_details.length > 0 &&
      technical_details.every(
        (item) => item.component && item.tech && item.notes,
      );

    // 7. Timeline
    const timelineValid =
      timeline.length > 0 &&
      timeline.every(
        (item) =>
          item.title &&
          item.durationCount &&
          item.durationUnit &&
          item.description,
      );

    // 8. Hardware Requirements

    // 11. Team Resources
    const teamValid =
      team_resource.length > 0 &&
      team_resource.every(
        (item) => item.role && item.count && item.responsibilities,
      );

    // 12. AMC (Only check if checked)

    // 13. Pricing
    const pricingValid =
      pricing.length > 0 &&
      pricing.every((item) => item.plan_name && item.price && item.description);

    // 14. Pricing Notes
    const priceNotesValid =
      pricing_notes.length > 0 &&
      pricing_notes.every((note) => note.trim() !== "");

    // 15. Payment Terms
    const paymentValid =
      payment_terms.length > 0 &&
      payment_terms.every((term) => term.trim() !== "");

    // 16. Terms and Conditions
    const termsValid =
      terms_conditions.length > 0 &&
      terms_conditions.every((term) => term.trim() !== "");

    return (
      basicFieldsValid &&
      submissionValid &&
      scopeValid &&
      platformValid &&
      techValid &&
      timelineValid &&
      teamValid &&
      pricingValid &&
      priceNotesValid &&
      paymentValid &&
      termsValid
    );
  }, [invoiceData, formFields, isAmcChecked]);

  // Initialize Data structure
  useEffect(() => {
    const resetData = {
      ...Object.fromEntries(formFields.map((f) => [f.id, ""])),
      Submission: getCurrentDate(),
      scope_work: [{ title: "", description: "" }],
      platform_delivery: [{ platform: "", description: "" }],
      core_features: [{ title: "", description: "" }],
      technical_details: [{ component: "", tech: "", notes: "" }],
      timeline: [
        { title: "", durationCount: "", durationUnit: "Days", description: "" },
      ],
      team_resource: [{ role: "", count: "", responsibilities: "" }],
      amc: [{ title: "", description: "" }],
      pricing: [{ plan_name: "", price: "", description: "" }],
      pricing_notes: [""],
      payment_terms: [""],
      terms_conditions: [""],
      // NEW SECTIONS
      hardware: [{ component: "", cost: "", purpose: "" }],
      hardware_notes: [""],
      clause_addon: [{ title: "Clause", content: [""] }], // Default to Clause
      image: null,
    };
    setInvoiceData(resetData);
    setIsAmcChecked(false);
  }, []);

  useEffect(() => {
    const fetchRoleId = async () => {
      if (role) {
        try {
          const response = await axios.get(`/api/get-role-id/`, {
            params: { role_name: role },
          });
          setUserRoleId(response.data.role_id);
        } catch (error) {
          console.error("Error fetching role ID:", error);
        }
      }
    };
    fetchRoleId();
  }, [role]);

  const pdfContentRef = useRef(null);

  const columns = [
    { field: "enquiry_id", headerName: "Enquiry ID" },
    { field: "folowed_date", headerName: "Date" },
    { field: "name", headerName: "Company Name" },
    { field: "status", headerName: "Status" },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params) => {
        const isGenerated = params.row.invoice_generated;
        return (
          <Button
            variant={isGenerated ? "outlined" : "contained"}
            size="small"
            onClick={() =>
              isGenerated
                ? handleEditInvoiceClick(params.row)
                : handleGenerateInvoiceClick(params.row)
            }
          >
            {isGenerated ? "Edit TCO" : "Generate TCO"}
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get("api/companies/");
        const filteredData = response.data.filter(
          (company) => company.status === "Sales Qualified",
        );
        setCompanies(filteredData);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleGenerateInvoiceClick = (company) => {
    setSelectedCompany(company);
    setIsEditMode(false);
    setEditingInvoiceId(null);

    const resetData = {
      ...Object.fromEntries(formFields.map((f) => [f.id, ""])),
      client_details: company.name || "",
      Submission: getCurrentDate(),
      scope_work: [{ title: "", description: "" }],
      platform_delivery: [{ platform: "", description: "" }],
      core_features: [{ title: "", description: "" }],
      technical_details: [{ component: "", tech: "", notes: "" }],
      timeline: [
        { title: "", durationCount: "", durationUnit: "Days", description: "" },
      ],
      team_resource: [{ role: "", count: "", responsibilities: "" }],
      amc: [{ title: "", description: "" }],
      pricing: [{ plan_name: "", price: "", description: "" }],
      pricing_notes: [""],
      payment_terms: [""],
      terms_conditions: [""],
      hardware: [{ component: "", cost: "", purpose: "" }],
      hardware_notes: [""],
      clause_addon: [{ title: "Clause", content: [""] }],
      image: null,
    };
    setInvoiceData(resetData);
    setIsAmcChecked(false);
    setIsDrawerOpen(true);
  };

  const handleEditInvoiceClick = async (company) => {
    setIsSubmitting(true);
    try {
      const findResponse = await axios.get(
        `/api/generate-invoice/?company_id=${company.id}`,
      );
      const invoices = findResponse.data;

      if (invoices.length > 0) {
        const invoice = invoices[0];
        setEditingInvoiceId(invoice.id);
        const detailResponse = await axios.get(`/api/invoices/${invoice.id}/`);
        const data = detailResponse.data;

        const hasValidAmc =
          data.amc && data.amc.length > 0 && data.amc[0].title.trim() !== "";

        setInvoiceData({
          ...data,
          client_details: data.client_details || company.name || "",
          scope_work: data.scope_work || [{ title: "", description: "" }],
          platform_delivery: data.platform_delivery || [
            { platform: "", description: "" },
          ],
          core_features: data.core_features || [{ title: "", description: "" }],
          technical_details: data.technical_details || [
            { component: "", tech: "", notes: "" },
          ],
          timeline: (
            data.timeline || [{ title: "", duration: "", description: "" }]
          ).map((t) => {
            const parts = (t.duration || "").split(" ");
            return {
              title: t.title,
              description: t.description,
              durationCount: parts[0] || "",
              durationUnit: parts[1] || "Days",
            };
          }),
          team_resource: data.team_resource || [
            { role: "", count: "", responsibilities: "" },
          ],
          amc: data.amc || [{ title: "", description: "" }],
          pricing: data.pricing || [
            { plan_name: "", price: "", description: "" },
          ],
          pricing_notes: data.pricing_notes || [""],
          payment_terms: data.payment_terms || [""],
          terms_conditions: data.terms_conditions || [""],
          Submission: data.Submission || getCurrentDate(),
          hardware: data.hardware || [{ component: "", cost: "", purpose: "" }],
          hardware_notes: data.hardware_notes || [""],
          clause_addon: data.clause_addon || [
            { title: "Clause", content: [""] },
          ],
        });

        setIsAmcChecked(hasValidAmc);
        setSelectedCompany(company);
        setIsEditMode(true);
        setIsDrawerOpen(true);
      } else {
        alert("No invoice found for this company to edit.");
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      alert("Failed to fetch invoice data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setCurrentPdfBlob(null);
  };

  const handleInputChange = (fieldId, value) => {
    setInvoiceData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleAmcCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsAmcChecked(checked);
    if (!checked) {
      setInvoiceData((prev) => ({
        ...prev,
        amc: [{ title: "", description: "" }],
      }));
    } else {
      setInvoiceData((prev) => ({
        ...prev,
        amc: [{ title: "", description: "" }],
      }));
    }
  };

  const handleListChange = (field, index, key, value) => {
    const newList = [...invoiceData[field]];
    newList[index][key] = value;
    setInvoiceData((prev) => ({ ...prev, [field]: newList }));
  };

  const handleSimpleListChange = (field, index, value) => {
    const newList = [...invoiceData[field]];
    newList[index] = value;
    setInvoiceData((prev) => ({ ...prev, [field]: newList }));
  };

  // Handler for Clauses (nested content)
  const handleClauseContentChange = (sectionIndex, contentIndex, value) => {
    const newSections = [...invoiceData.clause_addon];
    const newContent = [...newSections[sectionIndex].content];
    newContent[contentIndex] = value;

    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      content: newContent,
    };
    setInvoiceData((prev) => ({ ...prev, clause_addon: newSections }));
  };

  const addListItem = (field, template) => {
    setInvoiceData((prev) => ({
      ...prev,
      [field]: [...prev[field], template],
    }));
  };

  const removeListItem = (field, index) => {
    const newList = [...invoiceData[field]];
    newList.splice(index, 1);
    setInvoiceData((prev) => ({ ...prev, [field]: newList }));
  };

  // UPDATED: Add Clause Section - automatically selects available option
  const addClauseSection = () => {
    setInvoiceData((prev) => {
      // Find which options are already taken
      const existingTitles = prev.clause_addon.map((s) => s.title);
      // Find the first option from clauseOptions that is NOT in existingTitles
      const availableTitle =
        clauseOptions.find((opt) => !existingTitles.includes(opt)) ||
        clauseOptions[0];

      return {
        ...prev,
        clause_addon: [
          ...prev.clause_addon,
          { title: availableTitle, content: [""] },
        ],
      };
    });
  };

  const removeClauseSection = (index) => {
    const newList = [...invoiceData.clause_addon];
    newList.splice(index, 1);
    setInvoiceData((prev) => ({ ...prev, clause_addon: newList }));
  };

  const addClauseContent = (sectionIndex) => {
    const newSections = [...invoiceData.clause_addon];
    const newContent = [...newSections[sectionIndex].content, ""];
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      content: newContent,
    };
    setInvoiceData((prev) => ({ ...prev, clause_addon: newSections }));
  };

  const removeClauseContent = (sectionIndex, contentIndex) => {
    const newSections = [...invoiceData.clause_addon];
    const newContent = [...newSections[sectionIndex].content];
    newContent.splice(contentIndex, 1);
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      content: newContent,
    };
    setInvoiceData((prev) => ({ ...prev, clause_addon: newSections }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvoiceData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleShowPreview = async () => {
    if (!selectedCompany) {
      alert("Cannot preview invoice. Missing information.");
      return;
    }

    const cleanData = (list, checkKeys) =>
      list.filter((item) =>
        checkKeys.some((key) => item[key] && item[key].trim() !== ""),
      );

    const cleanSimpleList = (list) =>
      list.filter((item) => item && item.trim() !== "");

    const formattedTimeline = invoiceData.timeline.map((t) => ({
      title: t.title,
      description: t.description,
      duration: `${t.durationCount} ${t.durationUnit}`,
    }));

    const dataToPreview = {
      ...invoiceData,
      scope_work: cleanData(invoiceData.scope_work, ["title", "description"]),
      platform_delivery: cleanData(invoiceData.platform_delivery, ["platform"]),
      core_features: cleanData(invoiceData.core_features, ["title"]),
      technical_details: cleanData(invoiceData.technical_details, [
        "component",
      ]),
      timeline: cleanData(formattedTimeline, ["title"]),
      team_resource: cleanData(invoiceData.team_resource, ["role"]),
      amc: isAmcChecked ? invoiceData.amc : [],
      pricing: cleanData(invoiceData.pricing, ["plan_name"]),
      pricing_notes: cleanSimpleList(invoiceData.pricing_notes),
      payment_terms: cleanSimpleList(invoiceData.payment_terms),
      terms_conditions: cleanSimpleList(invoiceData.terms_conditions),
      // NEW SECTIONS
      hardware: cleanData(invoiceData.hardware, ["component"]),
      hardware_notes: cleanSimpleList(invoiceData.hardware_notes),
      clause_addon: invoiceData.clause_addon.filter(
        (sec) => sec.content && sec.content.some((c) => c && c.trim() !== ""),
      ),
      user_id: user_id,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        "/api/generate-ppt-quote/",
        dataToPreview,
        {
          responseType: "blob",
        },
      );

      setCurrentPdfBlob(response.data);
      const pdfUrl = URL.createObjectURL(response.data);
      setPreviewBlobUrl(pdfUrl);
      setPreviewData(dataToPreview);
      setShowPreview(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      alert("Failed to generate preview. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!previewBlobUrl) return;
    const link = document.createElement("a");
    link.href = previewBlobUrl;
    link.setAttribute(
      "download",
      `invoice_${selectedCompany?.name || "details"}.pdf`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleUpdateInvoice = async () => {
    if (!previewData || !editingInvoiceId) return;
    setIsSubmitting(true);
    try {
      await axios.patch(`/api/invoices/${editingInvoiceId}/`, previewData);
      alert("TCO updated successfully!");
      handleDrawerClose();
      setShowPreview(false);
    } catch (error) {
      console.error("Error updating invoice:", error);
      alert("Failed to update invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!previewData) return;
    setIsSubmitting(true);
    try {
      await axios.post("/api/invoices/", previewData);
      setCompanies((prevCompanies) =>
        prevCompanies.map((c) =>
          c.id === selectedCompany.id ? { ...c, invoice_generated: true } : c,
        ),
      );
      alert("TCO created successfully!");
      handleDrawerClose();
      setShowPreview(false);
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Failed to create invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  };

  const handleOpenSendMailModal = () => {
    if (!currentPdfBlob) {
      alert("PDF is not ready. Please regenerate invoice.");
      return;
    }
    setSendMailModalOpen(true);
  };

  const handleCloseSendMailModal = () => setSendMailModalOpen(false);

  const handleSendEmail = async () => {
    if (!currentPdfBlob) return;
    if (!emailData.recipientEmail) {
      alert("Please enter a recipient email.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("email", emailData.recipientEmail);
      if (emailData.ccEmail) formData.append("cc_email", emailData.ccEmail);
      formData.append("message", emailData.emailContent);
      formData.append("company_id", selectedCompany.export_company);
      formData.append(
        "pdf",
        currentPdfBlob,
        `invoice_${selectedCompany?.name || "details"}.pdf`,
      );

      await axios.post("/api/send-email-invoice/", formData);
      handleCloseSendMailModal();
      setShowPreview(false);
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const textareaStyle = {
    width: "100%",
    padding: "10px",
    boxSizing: "border-box",
    borderColor: "#c4c4c4",
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "4px",
    resize: "vertical",
    minHeight: "50px",
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <StyledTable columns={columns} data={companies} itemsPerPage={4} />

      <DrawerComponent
  open={isDrawerOpen}
  onClose={handleDrawerClose}
  title={`${isEditMode ? "Edit" : "Generate"} TCO for: ${selectedCompany?.name || "..."}`}
>
  <Box
    ref={pdfContentRef}
    sx={{
      width: "100%",
      height: "100%",
      overflowY: "auto",
      backgroundColor: "#f5f5f0",
      padding: 2,
    }}
  >
    <Box
      data-pdf-page
      sx={{
        width: "100%",
        backgroundColor: "white",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        marginBottom: 3,
        padding: 4,
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 2,
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold" }}>
          TCO Details
        </Typography>

        <Box
          sx={{
            width: "90%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 3,
          }}
        >
          {/* Basic Fields */}
          {formFields.map((field) => (
            <TextField
              key={field.id}
              label={field.label}
              type={field.type}
              multiline={field.multiline}
              rows={field.rows}
              value={invoiceData[field.id]}
              required
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              sx={{
                width: "100%",
                gridColumn: field.id === "summary" ? "1 / -1" : "auto",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": { borderColor: "#fb7102" },
                  "&.Mui-focused fieldset": { borderColor: "#fb7102" },
                },
              }}
              InputLabelProps={{
                ...(field.type === "date" ? { shrink: true } : {}),
                sx: {
                  "& .MuiInputLabel-asterisk": {
                    color: "#d32f2f", // Red color for asterisk
                  },
                },
              }}
            />
          ))}

          <TextField
            label="Submission Date"
            type="date"
            value={invoiceData.Submission}
            onChange={(e) => handleInputChange("Submission", e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: {
                "& .MuiInputLabel-asterisk": {
                  color: "#d32f2f",
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#fb7102" },
                "&.Mui-focused fieldset": { borderColor: "#fb7102" },
              },
            }}
            required
          />

          {/* Scope of Work */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Scope of Work
            </Typography>
            {invoiceData.scope_work.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Title ${index + 1}`}
                      value={item.title}
                      onChange={(e) =>
                        handleListChange("scope_work", index, "title", e.target.value)
                      }
                      fullWidth
                      required
                      // Force Red Asterisk
                      InputLabelProps={{
                        sx: {
                          "& .MuiInputLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#fb7102" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      {/* Wrapping TextareaAutosize to show Label and Red Star */}
                      <FormControl
                        fullWidth
                        required
                        sx={{ position: "relative", mt: 1 }}
                      >
                        <InputLabel
                          shrink
                          sx={{
                            transform: "translate(14px, -9px) scale(0.75)",
                            backgroundColor: "#ffffff",
                            padding: "0 4px",
                            zIndex: 1,
                            color: "rgba(0, 0, 0, 0.6)",
                            // Force Red Asterisk
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Description
                        </InputLabel>
                        <TextareaAutosize
                          minRows={2}
                          required
                          style={{
                            ...textareaStyle,
                            marginTop: "-5px", // Pull up to overlap with label
                          }}
                          value={item.description}
                          onChange={(e) =>
                            handleListChange(
                              "scope_work",
                              index,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </Grid>
                  {invoiceData.scope_work.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("scope_work", index)}
                      color="error"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.scope_work.length - 1 && (
                    <IconButton
                      onClick={() =>
                        addListItem("scope_work", { title: "", description: "" })
                      }
                      color="primary"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          {/* Platform Delivery */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Platform & Delivery
            </Typography>
            {invoiceData.platform_delivery.map((item, index) => {
              const selectedPlatforms = invoiceData.platform_delivery
                .map((p) => p.platform)
                .filter(Boolean);
              const availablePlatforms = platformOptions.filter(
                (p) => !selectedPlatforms.includes(p) || p === item.platform
              );
              return (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth required>
                        <InputLabel
                          sx={{
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Platform Type
                        </InputLabel>
                        <Select
                          value={item.platform}
                          onChange={(e) =>
                            handleListChange(
                              "platform_delivery",
                              index,
                              "platform",
                              e.target.value
                            )
                          }
                          label="Platform Type"
                        >
                          {availablePlatforms.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={7}
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <FormControl
                          fullWidth
                          required
                          sx={{ position: "relative", mt: 1 }}
                        >
                          <InputLabel
                            shrink
                            sx={{
                              transform: "translate(14px, -9px) scale(0.75)",
                              backgroundColor: "#ffffff",
                              padding: "0 4px",
                              zIndex: 1,
                              color: "rgba(0, 0, 0, 0.6)",
                              "& .MuiInputLabel-asterisk": {
                                color: "#d32f2f",
                              },
                            }}
                          >
                            Platform Description
                          </InputLabel>
                          <TextareaAutosize
                            minRows={2}
                            required
                            style={{
                              ...textareaStyle,
                              marginTop: "-5px",
                            }}
                            value={item.description}
                            onChange={(e) =>
                              handleListChange(
                                "platform_delivery",
                                index,
                                "description",
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                      </Box>
                    </Grid>
                    {invoiceData.platform_delivery.length > 1 && (
                      <IconButton
                        onClick={() => removeListItem("platform_delivery", index)}
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                    {index === invoiceData.platform_delivery.length - 1 &&
                      availablePlatforms.length > 1 && (
                        <IconButton
                          onClick={() =>
                            addListItem("platform_delivery", {
                              platform: "",
                              description: "",
                            })
                          }
                          color="primary"
                          sx={{ mt: 1, ml: 2 }}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {/* Core Features - EXCLUDED AS REQUESTED */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Core Features
            </Typography>
            {invoiceData.core_features.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label={`Feature Title ${index + 1}`}
                      value={item.title}
                      onChange={(e) =>
                        handleListChange("core_features", index, "title", e.target.value)
                      }
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#fb7102" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={7}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <TextareaAutosize
                        placeholder="Feature Description"
                        minRows={2}
                        style={textareaStyle}
                        value={item.description}
                        onChange={(e) =>
                          handleListChange(
                            "core_features",
                            index,
                            "description",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                  </Grid>
                  {invoiceData.core_features.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("core_features", index)}
                      color="error"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.core_features.length - 1 && (
                    <IconButton
                      onClick={() =>
                        addListItem("core_features", { title: "", description: "" })
                      }
                      color="primary"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          {/* Technical Details */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Technical Details
            </Typography>
            {invoiceData.technical_details.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Component"
                      required
                      value={item.component}
                      onChange={(e) =>
                        handleListChange(
                          "technical_details",
                          index,
                          "component",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{
                        sx: {
                          "& .MuiInputLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#fb7102" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Tech / Approach"
                      required
                      value={item.tech}
                      onChange={(e) =>
                        handleListChange(
                          "technical_details",
                          index,
                          "tech",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{
                        sx: {
                          "& .MuiInputLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#fb7102" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <FormControl
                        fullWidth
                        required
                        sx={{ position: "relative", mt: 1 }}
                      >
                        <InputLabel
                          shrink
                          sx={{
                            transform: "translate(14px, -9px) scale(0.75)",
                            backgroundColor: "#ffffff",
                            padding: "0 4px",
                            zIndex: 1,
                            color: "rgba(0, 0, 0, 0.6)",
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Notes
                        </InputLabel>
                        <TextareaAutosize
                          minRows={2}
                          required
                          style={{
                            ...textareaStyle,
                            marginTop: "-5px",
                          }}
                          value={item.notes}
                          onChange={(e) =>
                            handleListChange(
                              "technical_details",
                              index,
                              "notes",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </Grid>
                  {invoiceData.technical_details.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("technical_details", index)}
                      color="error"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.technical_details.length - 1 && (
                    <IconButton
                      onClick={() =>
                        addListItem("technical_details", {
                          component: "",
                          tech: "",
                          notes: "",
                        })
                      }
                      color="primary"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          {/* Timeline */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Project Timeline
            </Typography>
            {invoiceData.timeline.map((item, index) => (
              <Card key={index} sx={{ mb: 2, border: "1px solid #cacacaff" }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Phase Title"
                        value={item.title}
                        required
                        onChange={(e) =>
                          handleListChange(
                            "timeline",
                            index,
                            "title",
                            e.target.value
                          )
                        }
                        fullWidth
                        InputLabelProps={{
                          sx: {
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Count"
                        type="number"
                        required
                        value={item.durationCount}
                        onChange={(e) =>
                          handleListChange(
                            "timeline",
                            index,
                            "durationCount",
                            e.target.value
                          )
                        }
                        fullWidth
                        InputLabelProps={{
                          sx: {
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required>
                        <InputLabel
                          sx={{
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Duration Unit
                        </InputLabel>
                        <Select
                          value={item.durationUnit}
                          onChange={(e) =>
                            handleListChange(
                              "timeline",
                              index,
                              "durationUnit",
                              e.target.value
                            )
                          }
                          label="Duration Unit"
                        >
                          {days.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <FormControl
                        fullWidth
                        required
                        sx={{ position: "relative", mt: 1 }}
                      >
                        <InputLabel
                          shrink
                          sx={{
                            transform: "translate(14px, -9px) scale(0.75)",
                            backgroundColor: "#ffffff",
                            padding: "0 4px",
                            zIndex: 1,
                            color: "rgba(0, 0, 0, 0.6)",
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Description
                        </InputLabel>
                        <TextareaAutosize
                          minRows={2}
                          required
                          style={{
                            ...textareaStyle,
                            marginTop: "-5px",
                          }}
                          value={item.description}
                          onChange={(e) =>
                            handleListChange(
                              "timeline",
                              index,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </Grid>
                  {invoiceData.timeline.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("timeline", index)}
                      color="error"
                      sx={{ mt: 1 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.timeline.length - 1 && (
                    <IconButton
                      onClick={() =>
                        addListItem("timeline", {
                          title: "",
                          durationCount: "",
                          durationUnit: "Days",
                          description: "",
                        })
                      }
                      color="primary"
                      sx={{ mt: 1 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Hardware Requirements - EXCLUDED AS REQUESTED */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Hardware Requirements
            </Typography>
            <Box sx={{ border: "1px solid #e0e0e0", p: 2, borderRadius: "8px" }}>
              {invoiceData.hardware.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Component"
                        value={item.component}
                        onChange={(e) =>
                          handleListChange("hardware", index, "component", e.target.value)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Cost"
                        value={item.cost}
                        onChange={(e) =>
                          handleListChange("hardware", index, "cost", e.target.value)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={5}
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <TextareaAutosize
                          placeholder="Purpose"
                          minRows={2}
                          style={textareaStyle}
                          value={item.purpose}
                          onChange={(e) =>
                            handleListChange(
                              "hardware",
                              index,
                              "purpose",
                              e.target.value
                            )
                          }
                        />
                      </Box>
                    </Grid>
                    {invoiceData.hardware.length > 1 && (
                      <IconButton
                        onClick={() => removeListItem("hardware", index)}
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                    {index === invoiceData.hardware.length - 1 && (
                      <IconButton
                        onClick={() =>
                          addListItem("hardware", {
                            component: "",
                            cost: "",
                            purpose: "",
                          })
                        }
                        color="primary"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Box>
              ))}

              {/* Hardware Notes - Included */}
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, fontWeight: "bold", fontSize: 18 }}
              >
                Hardware Notes
              </Typography>
              {invoiceData.hardware_notes.map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    label={`Note ${index + 1}`}
                    value={item}
                    onChange={(e) =>
                      handleSimpleListChange("hardware_notes", index, e.target.value)
                    }
                    fullWidth
                    multiline
                  
                    InputLabelProps={{
                      sx: {
                        "& .MuiInputLabel-asterisk": {
                          color: "#d32f2f",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": { borderColor: "#fb7102" },
                      },
                    }}
                  />
                  {invoiceData.hardware_notes.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("hardware_notes", index)}
                      color="error"
                      sx={{ ml: 1 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.hardware_notes.length - 1 && (
                    <IconButton
                      onClick={() => addListItem("hardware_notes", "")}
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Clauses and Add-ons - EXCLUDED AS REQUESTED */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Clauses and Add-ons
            </Typography>
            {invoiceData.clause_addon.map((section, sIndex) => {
              const selectedTitles = invoiceData.clause_addon
                .filter((_, idx) => idx !== sIndex)
                .map((sec) => sec.title);
              const availableOptions = clauseOptions.filter(
                (opt) => !selectedTitles.includes(opt)
              );

              return (
                <Box
                  key={sIndex}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth required>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={section.title}
                          onChange={(e) =>
                            handleListChange(
                              "clause_addon",
                              sIndex,
                              "title",
                              e.target.value
                            )
                          }
                          label="Type"
                        >
                          {availableOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ width: "100%" }}>
                        {section.content.map((cItem, cIndex) => (
                          <Box
                            key={cIndex}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <TextareaAutosize
                              placeholder="Content"
                              minRows={1}
                              style={textareaStyle}
                              value={cItem}
                              onChange={(e) =>
                                handleClauseContentChange(
                                  sIndex,
                                  cIndex,
                                  e.target.value
                                )
                              }
                              sx={{ flexGrow: 1 }}
                            />
                            {section.content.length > 1 && (
                              <IconButton
                                onClick={() =>
                                  removeClauseContent(sIndex, cIndex)
                                }
                                color="error"
                                size="small"
                              >
                                <RemoveIcon />
                              </IconButton>
                            )}
                            {cIndex === section.content.length - 1 && (
                              <IconButton
                                onClick={() => addClauseContent(sIndex)}
                                color="primary"
                                size="small"
                              >
                                <AddIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                    {invoiceData.clause_addon.length > 1 && (
                      <IconButton
                        onClick={() => removeClauseSection(sIndex)}
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                    {sIndex === invoiceData.clause_addon.length - 1 &&
                      availableOptions.length > 1 && (
                        <IconButton
                          onClick={() => addClauseSection()}
                          color="primary"
                          sx={{ mt: 1, ml: 2 }}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {/* Team Resources */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Team Resources
            </Typography>
            {invoiceData.team_resource.map((resource, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={allPossibleRoles}
                      value={resource.role}
                      onChange={(e, newValue) =>
                        handleListChange("team_resource", index, "role", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Role (Searchable)"
                          variant="outlined"
                          required
                          InputLabelProps={{
                            sx: {
                              "& .MuiInputLabel-asterisk": {
                                color: "#d32f2f",
                              },
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#fb7102",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Count"
                      type="number"
                      required
                      value={resource.count}
                      onChange={(e) =>
                        handleListChange(
                          "team_resource",
                          index,
                          "count",
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{
                        sx: {
                          "& .MuiInputLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": { borderColor: "#fb7102" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <FormControl
                        fullWidth
                        required
                        sx={{ position: "relative", mt: 1 }}
                      >
                        <InputLabel
                          shrink
                          sx={{
                            transform: "translate(14px, -9px) scale(0.75)",
                            backgroundColor: "#ffffff",
                            padding: "0 4px",
                            zIndex: 1,
                            color: "rgba(0, 0, 0, 0.6)",
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          }}
                        >
                          Responsibilities
                        </InputLabel>
                        <TextareaAutosize
                          minRows={3}
                          required
                          style={{
                            ...textareaStyle,
                            marginTop: "-5px",
                          }}
                          value={resource.responsibilities}
                          onChange={(e) =>
                            handleListChange(
                              "team_resource",
                              index,
                              "responsibilities",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </Grid>
                  {invoiceData.team_resource.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("team_resource", index)}
                      color="error"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.team_resource.length - 1 && (
                    <IconButton
                      onClick={() =>
                        addListItem("team_resource", {
                          role: "",
                          count: "",
                          responsibilities: "",
                        })
                      }
                      color="primary"
                      sx={{ mt: 1, ml: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>

          {/* AMC */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              AMC & Maintenance
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAmcChecked}
                    onChange={handleAmcCheckboxChange}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label="AMC"
              />
            </FormGroup>

            {isAmcChecked && (
              <>
                {invoiceData.amc.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      border: "1px solid #e0e0e0",
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          label="Service Title"
                          value={item.title}
                          onChange={(e) =>
                            handleListChange("amc", index, "title", e.target.value)
                          }
                          fullWidth
                          required
                          InputLabelProps={{
                            sx: {
                              "& .MuiInputLabel-asterisk": {
                                color: "#d32f2f",
                              },
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#fb7102",
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={7}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <FormControl
                            fullWidth
                            required
                            sx={{ position: "relative", mt: 1 }}
                          >
                            <InputLabel
                              shrink
                              sx={{
                                transform: "translate(14px, -9px) scale(0.75)",
                                backgroundColor: "#ffffff",
                                padding: "0 4px",
                                zIndex: 1,
                                color: "rgba(0, 0, 0, 0.6)",
                                "& .MuiInputLabel-asterisk": {
                                  color: "#d32f2f",
                                },
                              }}
                            >
                              Description
                            </InputLabel>
                            <TextareaAutosize
                              minRows={2}
                              required
                              style={{
                                ...textareaStyle,
                                marginTop: "-5px",
                              }}
                              value={item.description}
                              onChange={(e) =>
                                handleListChange(
                                  "amc",
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>
                        </Box>
                      </Grid>
                      {invoiceData.amc.length > 1 && (
                        <IconButton
                          onClick={() => removeListItem("amc", index)}
                          color="error"
                          sx={{ mt: 1, ml: 2 }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                      {index === invoiceData.amc.length - 1 && (
                        <IconButton
                          onClick={() =>
                            addListItem("amc", { title: "", description: "" })
                          }
                          color="primary"
                          sx={{ mt: 1, ml: 2 }}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Box>
                ))}
              </>
            )}
          </Box>

          {/* Pricing */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Pricing
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                p: 2,
                borderRadius: "8px",
              }}
            >
              {invoiceData.pricing.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Plan Name"
                        value={item.plan_name}
                        onChange={(e) =>
                          handleListChange(
                            "pricing",
                            index,
                            "plan_name",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        InputLabelProps={{
                          sx: {
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Price"
                        value={item.price}
                        onChange={(e) =>
                          handleListChange("pricing", index, "price", e.target.value)
                        }
                        fullWidth
                        required
                        type="number"
                        InputLabelProps={{
                          sx: {
                            "& .MuiInputLabel-asterisk": {
                              color: "#d32f2f",
                            },
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#fb7102",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={5}
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <FormControl
                          fullWidth
                          required
                          sx={{ position: "relative", mt: 1 }}
                        >
                          <InputLabel
                            shrink
                            sx={{
                              transform: "translate(14px, -9px) scale(0.75)",
                              backgroundColor: "#ffffff",
                              padding: "0 4px",
                              zIndex: 1,
                              color: "rgba(0, 0, 0, 0.6)",
                              "& .MuiInputLabel-asterisk": {
                                color: "#d32f2f",
                              },
                            }}
                          >
                            Description
                          </InputLabel>
                          <TextareaAutosize
                            minRows={2}
                            required
                            style={{
                              ...textareaStyle,
                              marginTop: "-5px",
                            }}
                            value={item.description}
                            onChange={(e) =>
                              handleListChange(
                                "pricing",
                                index,
                                "description",
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                      </Box>
                    </Grid>
                    {invoiceData.pricing.length > 1 && (
                      <IconButton
                        onClick={() => removeListItem("pricing", index)}
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                    {index === invoiceData.pricing.length - 1 && (
                      <IconButton
                        onClick={() =>
                          addListItem("pricing", {
                            plan_name: "",
                            price: "",
                            description: "",
                          })
                        }
                        color="primary"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Box>
              ))}

              {/* Pricing Notes */}
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, fontWeight: "bold", fontSize: 18 }}
              >
                Pricing Notes
              </Typography>
              {invoiceData.pricing_notes.map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    label={`Note ${index + 1}`}
                    value={item}
                    onChange={(e) =>
                      handleSimpleListChange("pricing_notes", index, e.target.value)
                    }
                    fullWidth
                    required
                    multiline
                    InputLabelProps={{
                      sx: {
                        "& .MuiInputLabel-asterisk": {
                          color: "#d32f2f",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": { borderColor: "#fb7102" },
                      },
                    }}
                  />
                  {invoiceData.pricing_notes.length > 1 && (
                    <IconButton
                      onClick={() => removeListItem("pricing_notes", index)}
                      color="error"
                      sx={{ ml: 1 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {index === invoiceData.pricing_notes.length - 1 && (
                    <IconButton
                      onClick={() => addListItem("pricing_notes", "")}
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Payment Terms */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Payment Terms
            </Typography>
            {invoiceData.payment_terms.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <TextField
                  label={`Term ${index + 1}`}
                  value={item}
                  onChange={(e) =>
                    handleSimpleListChange("payment_terms", index, e.target.value)
                  }
                  fullWidth
                  required
                  multiline
                  InputLabelProps={{
                    sx: {
                      "& .MuiInputLabel-asterisk": {
                        color: "#d32f2f",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "#fb7102" },
                    },
                  }}
                />
                {invoiceData.payment_terms.length > 1 && (
                  <IconButton
                    onClick={() => removeListItem("payment_terms", index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
                {index === invoiceData.payment_terms.length - 1 && (
                  <IconButton
                    onClick={() => addListItem("payment_terms", "")}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          {/* Terms and Conditions */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Terms and Conditions
            </Typography>
            {invoiceData.terms_conditions.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <TextField
                  label={`Condition ${index + 1}`}
                  value={item}
                  onChange={(e) =>
                    handleSimpleListChange("terms_conditions", index, e.target.value)
                  }
                  fullWidth
                  multiline
                  required
                  InputLabelProps={{
                    sx: {
                      "& .MuiInputLabel-asterisk": {
                        color: "#d32f2f",
                      },
                    },
                  }}
                  sx={{
                    flexGrow: 1,
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "#fb7102" },
                    },
                  }}
                />
                {invoiceData.terms_conditions.length > 1 && (
                  <IconButton
                    onClick={() => removeListItem("terms_conditions", index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
                {index === invoiceData.terms_conditions.length - 1 && (
                  <IconButton
                    onClick={() => addListItem("terms_conditions", "")}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          {/* Image Upload */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: 18 }}
            >
              Upload Image
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{
                  mr: 2,
                  "&:hover": { border: "2px solid #d15e00ff" },
                }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                  required={!invoiceData.image}
                />
              </Button>
              {invoiceData.image && (
                <Chip
                  icon={<ImageIcon />}
                  label="Image uploaded"
                  onDelete={() =>
                    setInvoiceData((prev) => ({ ...prev, image: null }))
                  }
                  color="primary"
                />
              )}
            </Box>
            {invoiceData.image && (
              <Box sx={{ mt: 2, maxWidth: "200px" }}>
                <img
                  src={invoiceData.image}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleShowPreview}
          disabled={isSubmitting || !isFormValid}
          sx={{
            px: 5,
            py: 1.5,
            background: "#fb7102",
            fontWeight: "bold",
            "&:hover": { background: "#d15e00ff" },
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={24} sx={{ color: "white", mr: 1 }} />
              Generating...
            </>
          ) : isEditMode ? (
            "Update TCO"
          ) : (
            "Generate TCO"
          )}
        </Button>
      </Box>
    </Box>
  </Box>
</DrawerComponent>

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={handleCancelPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Preview & Confirm</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your PDF has been generated. Please review it in viewer below. If it
            looks correct, confirm your action.
          </Typography>
          <PdfViewer blobUrl={previewBlobUrl} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancelPreview}
          >
            Cancel
          </Button>

          {userRoleId === 1 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={isSubmitting}
            >
              Download PDF
            </Button>
          )}

          <Button
            variant="contained"
            color="success"
            startIcon={<EmailIcon />}
            onClick={handleOpenSendMailModal}
            disabled={isSubmitting}
          >
            Send Mail
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Mail Modal */}
      <Modal open={sendMailModalOpen} onClose={handleCloseSendMailModal}>
        <Box
          sx={{
            padding: 4,
            width: 400,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 3,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Send TCO Email
          </Typography>
          <TextField
            label="Recipient Email"
            fullWidth
            value={emailData.recipientEmail}
            onChange={(e) =>
              setEmailData((prev) => ({
                ...prev,
                recipientEmail: e.target.value,
              }))
            }
            margin="normal"
            InputProps={{
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "orange",
                  borderWidth: 2,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d15e00ff",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fb7102",
                },
              },
            }}
          />
          <TextField
            label="CC Email (optional)"
            fullWidth
            value={emailData.ccEmail}
            onChange={(e) =>
              setEmailData((prev) => ({ ...prev, ccEmail: e.target.value }))
            }
            margin="normal"
            InputProps={{
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "orange",
                  borderWidth: 2,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d15e00ff",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fb7102",
                },
              },
            }}
          />
          <TextField
            label="Email Content"
            fullWidth
            multiline
            rows={4}
            value={emailData.emailContent}
            onChange={(e) =>
              setEmailData((prev) => ({
                ...prev,
                emailContent: e.target.value,
              }))
            }
            margin="normal"
            InputProps={{
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "orange",
                  borderWidth: 2,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d15e00ff",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fb7102",
                },
              },
            }}
          />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}
          >
            <Button
              onClick={handleCloseSendMailModal}
              color="secondary"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} /> : <EmailIcon />
              }
            >
              Send
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default GenerateTCO;
